import { useEffect, useState } from "react";
import useApiCallService from "../../Common/Helpers/ApiCall";
import SortableList from "../../Common/Components/Lists/SortableList";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { Button, Card, Col, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ADMIN_PATHS } from "../../config";
import AdminResponseHonorarium from "../Models/Response/AdminResponseHonorarium";
import { useTranslation } from "react-i18next";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { ToCustomDecimal } from "../../AppSystem/Helpers/ToCustomDecimal";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { UrlStructure } from "../../AppSystem/Helpers/UrlStructure";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";

const AdminHonorariumList = ({ userId = null, isSuperAdmin = null, setActiveTab = null, returnPath = null }) => {
    isSuperAdmin = CheckUserRole("SuperAdmin");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ApiCall } = useApiCallService();
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [deleteApiResponse, setDeleteApiResponse] = useState(null);
    const [honorariumId, setHonorariumId] = useState(null);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('honorarium.honorarium'));
        setPageTitleKey('honorarium.honorarium');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

  let idFromUrl = "";
  if (window.location.pathname.includes("admin/user-list/user")) {
    idFromUrl = new URLSearchParams(window.location.search).get("id");
  }
  const baseUrl = "/honorarium/admin/search/filter";

  // isReadOnly si viene userId
  const isReadOnly = userId && userId !== "" && userId !== "undefined";

  let permission = false;
  if (
    CheckUserPermission("admin") ||
    CheckUserPermission("admin.honorarium") ||
    CheckUserPermission("admin.honorarium.create")
  ) {
    permission = true;
  }

  let permissionDelete = false;
  if (
    CheckUserPermission("admin") ||
    CheckUserPermission("admin.honorarium") ||
    CheckUserPermission("admin.honorarium.delete")
  ) {
    permissionDelete = true;
  }

  const renderActionColumn = (item) => {
    return (
      <>
        {item.status === "Förslag" ? (
          <>
            {!isReadOnly && permissionDelete === true ? (
              <Button
                className="box-red border-0 position-relative me-2 rounded"
                onClick={() => {
                  setDisplayDeleteModal(true);
                  setHonorariumId(item.id);
                }}
              >
                <i className="bx bx-x-circle fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">
                  {t("button.delete")}
                </div>
              </Button>
            ) : (
              ""
            )}
            {!isReadOnly && (
              <Button
                className="bg-green-light text-green border-0 position-relative"
                onClick={() => {
                  navigate(`${ADMIN_PATHS.HONORARIUM_UPDATE}?id=${item.id}`);
                }}
              >
                <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">{t("button.edit")}</div>
              </Button>
            )}
          </>
        ) : (
          <CustomButton
            icon="bx bx-show py-1"
            iconColor="#157553"
            hoverIconColor="white"
            hoverButtonColor="#A3C2B8"
            iconSize={4}
            buttonColor="#cde0da"
            tooltipText={t("button.info")}
            customClasses="rounded"
            buttonBorder={false}
            id={item.id}
            onClick={() => {
              navigate(`${ADMIN_PATHS.HONORARIUM_INFO}?id=${item.id}`, {
                state: { returnTo: returnPath, userId: userId },
              });
            }}
          />
        )}
      </>
    );
  };

  // HANDLE MODAL DELETE
  const handleModalDelete = async () => {
    const apiCallOptions = {
      method: "delete",
      url: `/honorarium`,
      data: {
        id: honorariumId,
      },
    };
    try {
      await ApiCall(apiCallOptions, setDeleteApiResponse);
      setDisplayDeleteModal(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (deleteApiResponse !== null) {
      // Delete localStorage keys related to honorariumList
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          Object.keys(window.localStorage).forEach((key) => {
            if (key.startsWith("honorariumList")) {
              window.localStorage.removeItem(key);
            }
          });
        }
      } catch (e) {
        console.warn("Error clearing honorariumList keys from localStorage", e);
      }

      CustomReload(ADMIN_PATHS.HONORARIUM_LIST);
    }
  }, [deleteApiResponse]);

  const modalContent = <div>{`${t("validationMessage.modalVar")}?`}</div>;

  const modalFooter = (
    <>
      <Button className="box-green border-0" onClick={handleModalDelete}>
        {t("button.delete")}
      </Button>
      <Button
        className="box-red border-0"
        onClick={() => setDisplayDeleteModal(false)}
      >
        {t("button.cancel")}
      </Button>
    </>
  );

  const renderButtonColumn = (item) => {
    return (
      <>
        {permission && !isReadOnly ? (
          <Col lg={12} className="mt-4 px-3">
            <CustomButton
              customClasses="rounded"
              text={t("honorarium.newHonorarium")}
              icon="bx bx-plus pe-2"
              onClick={() => {
                navigate(ADMIN_PATHS.HONORARIUM);
              }}
            />
          </Col>
        ) : (
          ""
        )}
      </>
    );
  };

  const columns = [
    "visualId",
    "createdAt",
    "userName",
    "orgName",
    "costPlace",
    "firstPayment",
    "paymentInterval",
    {
      field: "annualHonorarium",
      label: t("honorarium.annual"),
      insideClass: (item) =>
        item.annualHonorarium >= 0
          ? "text-green text-end fixed-width"
          : "text-red text-end fixed-width", //w-50
      value: (item) =>
        item.annualHonorarium
          ? ToCustomDecimal(item.annualHonorarium) + " kr"
          : "",
      type: "number",
    },
    "status",
  ];

  const columnsToExport = [
    "visualId",
    "createdAt",
    "userName",
    "orgName",
    "costPlace",
    "firstPayment",
    "paymentInterval",
    "annualHonorarium",
    "status",
  ];

  if (isSuperAdmin === true) {
    columns.unshift("id");
  }
  const listOptions = {
    model: AdminResponseHonorarium,
    columns: columns,
    actions: {
      actionColumn: renderActionColumn,
      addButton: renderButtonColumn,
    },
    exportIsAvailable: true,
    urlGetListItemsToExport: baseUrl + (idFromUrl ? `?id=${idFromUrl}` : ""),
    nameFile: t("honorarium.honorarium"),
    ignoreFields: columnsToExport,
    modelType: "Honorarium",
    filterTestIsAvailable: true,
    urlGetSearch: baseUrl,
    viewName: "AdminHonorarium",
    ignoreUrlParams: !!idFromUrl,
  };

    return (
        <Col lg="12" className='card card-backend p-4 mb-5'>    
         <CustomModal isOpen={displayDeleteModal} toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                                 body={modalContent} footer={modalFooter}/>     
                <Row>
                    <Col lg={6}>
                    <h2 className="pb-2 ps-1 rokkitt-font">{t('sidebar.honorarium')}</h2>
                    </Col>
                    <Col lg={6} className="text-end">
                        <Button
                            className="bg-green"
                            onClick={() => {
                                if (setActiveTab) {
                                    setActiveTab("6");
                                } else {
                                    navigate(`${ADMIN_PATHS.HONORARIUM_HISTORY_LIST}`);
                                }
                            }}
                            >
                            {t('openPayment.history')}
                        </Button>
                    </Col>
                </Row>
                <SortableList
                    listOptions={listOptions}
                />
        </Col>
    );
};

export default AdminHonorariumList;
